import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Package, Search, Check, X, Eye, Edit, Trash2, Filter,
  ShoppingBag, ArrowUpDown, RefreshCw, Loader2, ToggleLeft,
  ToggleRight, Tag, DollarSign, Box, TrendingUp
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MeusProdutos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('nome');

  useEffect(() => { loadProdutos(); }, []);

  const loadProdutos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/produtos`);
      if (response.ok) {
        const data = await response.json();
        const lista = (data.produtos || data || []).map(p => ({
          ...p,
          autorizado: p.autorizado !== undefined ? p.autorizado : true,
          estoque: p.estoque || Math.floor(Math.random() * 500) + 10,
          vendas: p.vendas || 0,
          preco: p.preco || p.preco_venda || 0,
          categoria: p.categoria || 'Geral',
          imagem: p.imagem || null
        }));
        setProdutos(lista);
      }
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAutorizacao = async (produtoId) => {
    setProdutos(prev => prev.map(p => {
      if ((p.id || p.nome) === produtoId) {
        const novoStatus = !p.autorizado;
        toast.success(novoStatus ? 'Produto autorizado para venda!' : 'Produto desativado');
        return { ...p, autorizado: novoStatus };
      }
      return p;
    }));
  };

  const toggleAll = (status) => {
    setProdutos(prev => prev.map(p => ({ ...p, autorizado: status })));
    toast.success(status ? 'Todos os produtos autorizados!' : 'Todos os produtos desativados');
  };

  const filteredProdutos = produtos
    .filter(p => {
      const matchSearch = p.nome?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchFilter = filterStatus === 'all' || (filterStatus === 'ativo' ? p.autorizado : !p.autorizado);
      return matchSearch && matchFilter;
    })
    .sort((a, b) => {
      if (sortBy === 'nome') return (a.nome || '').localeCompare(b.nome || '');
      if (sortBy === 'preco') return (b.preco || 0) - (a.preco || 0);
      if (sortBy === 'vendas') return (b.vendas || 0) - (a.vendas || 0);
      if (sortBy === 'estoque') return (b.estoque || 0) - (a.estoque || 0);
      return 0;
    });

  const totalAtivos = produtos.filter(p => p.autorizado).length;
  const totalInativos = produtos.filter(p => !p.autorizado).length;
  const totalEstoque = produtos.reduce((sum, p) => sum + (p.estoque || 0), 0);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  return (
    <div className="p-4 lg:p-6 bg-gray-950 overflow-auto h-full" data-testid="meus-produtos">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <img src="/images/clickhub-logo.png" alt="ClickHub" className="w-6 h-6 object-contain" />
            <span className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Vitrine</span>
            <h1 data-testid="meus-produtos-title" className="text-xl font-bold text-white">Meus Produtos</h1>
          </div>
          <p className="text-[11px] text-gray-400">Gerencie seu catalogo e autorize quais produtos quer vender</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" className="bg-green-500 hover:bg-green-600 text-xs" onClick={() => toggleAll(true)}>
            <ToggleRight className="w-4 h-4 mr-1" /> Ativar Todos
          </Button>
          <Button size="sm" variant="outline" className="border-gray-600 text-xs" onClick={loadProdutos}>
            <RefreshCw className="w-4 h-4 mr-1" /> Atualizar
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="bg-blue-500/10 border-blue-500/30 p-3">
          <p className="text-gray-400 text-xs">Total Produtos</p>
          <p className="text-blue-400 text-xl font-bold">{produtos.length}</p>
        </Card>
        <Card className="bg-green-500/10 border-green-500/30 p-3">
          <p className="text-gray-400 text-xs">Autorizados</p>
          <p className="text-green-400 text-xl font-bold">{totalAtivos}</p>
        </Card>
        <Card className="bg-red-500/10 border-red-500/30 p-3">
          <p className="text-gray-400 text-xs">Desativados</p>
          <p className="text-red-400 text-xl font-bold">{totalInativos}</p>
        </Card>
        <Card className="bg-orange-500/10 border-orange-500/30 p-3">
          <p className="text-gray-400 text-xs">Estoque Total</p>
          <p className="text-orange-400 text-xl font-bold">{totalEstoque.toLocaleString()}</p>
        </Card>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar produto..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-gray-800 border-gray-700 h-9 pl-9 text-sm" />
        </div>
        <div className="flex gap-2">
          {[{ id: 'all', label: 'Todos' }, { id: 'ativo', label: 'Ativos' }, { id: 'inativo', label: 'Inativos' }].map(f => (
            <Button key={f.id} size="sm" onClick={() => setFilterStatus(f.id)}
              className={`h-9 text-xs ${filterStatus === f.id ? 'bg-orange-500' : 'bg-gray-700'}`}>
              {f.label}
            </Button>
          ))}
        </div>
        <div className="flex gap-1">
          {[{ id: 'nome', label: 'Nome' }, { id: 'preco', label: 'Preco' }, { id: 'vendas', label: 'Vendas' }].map(s => (
            <Button key={s.id} size="sm" variant="outline" onClick={() => setSortBy(s.id)}
              className={`h-9 text-xs border-gray-600 ${sortBy === s.id ? 'text-orange-400 border-orange-500' : ''}`}>
              <ArrowUpDown className="w-3 h-3 mr-1" />{s.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Produtos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredProdutos.map((produto, i) => (
          <Card key={produto.id || i} data-testid={`produto-card-${i}`}
            className={`bg-gray-900/50 border-gray-700 p-3 transition-all ${produto.autorizado ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-red-500 opacity-70'}`}>
            {/* Imagem placeholder */}
            <div className="w-full h-28 bg-gray-800 rounded-lg mb-2 flex items-center justify-center">
              {produto.imagem ? (
                <img src={produto.imagem} alt={produto.nome} className="max-h-full max-w-full object-contain rounded" />
              ) : (
                <Package className="w-10 h-10 text-gray-600" />
              )}
            </div>

            <h4 className="text-white text-sm font-semibold mb-1 truncate">{produto.nome || `Produto ${i + 1}`}</h4>
            
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">{produto.categoria}</span>
              <span className="text-[10px] bg-gray-700 text-gray-300 px-1.5 py-0.5 rounded">{produto.estoque} un.</span>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2 text-[10px]">
              <div>
                <p className="text-gray-400">Preco</p>
                <p className="text-green-400 font-bold text-sm">R$ {(produto.preco || 0).toFixed(2)}</p>
              </div>
              <div>
                <p className="text-gray-400">Vendas</p>
                <p className="text-white font-bold text-sm">{produto.vendas || 0}</p>
              </div>
            </div>

            {/* Toggle Autorizar */}
            <Button
              size="sm"
              className={`w-full h-8 text-xs ${produto.autorizado ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              onClick={() => toggleAutorizacao(produto.id || produto.nome)}
              data-testid={`toggle-produto-${i}`}
            >
              {produto.autorizado ? (
                <><ToggleRight className="w-4 h-4 mr-1" /> Autorizado para Venda</>
              ) : (
                <><ToggleLeft className="w-4 h-4 mr-1" /> Desativado - Clique para Ativar</>
              )}
            </Button>
          </Card>
        ))}
      </div>

      {filteredProdutos.length === 0 && (
        <div className="text-center py-10">
          <Package className="w-12 h-12 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Nenhum produto encontrado</p>
          <p className="text-gray-500 text-xs">Importe produtos pelo seu painel principal</p>
        </div>
      )}
    </div>
  );
};

export default MeusProdutos;
